import { useCallback, useEffect, useState } from 'react';
import { VISITOR_NAME_AAD_CID, type ConversationSummary } from '@kairelo/shared';
import { invitations } from '../lib/api.js';
import {
  listStoredConversationIds,
  loadConversationKey,
  loadInvitationUrls,
} from '../lib/keystore.js';
import { decryptPayload } from '../lib/crypto.js';
import type { ChatConversation } from './types.js';

/**
 * The owner's conversation directory.
 *
 * The server returns operational status and ciphertext; names are decrypted
 * here, in the browser that holds the key. That distinction is the whole point:
 * a name shown in this list was never readable to the server, and a conversation
 * whose key lives in another browser profile stays deliberately nameless.
 */

function previewFor(summary: ConversationSummary, hasKey: boolean, name: string | null): string {
  if (summary.status === 'pending') return 'Invitation not opened yet';
  if (summary.status === 'expired') return 'This conversation has ended';
  if (summary.status === 'deleted') return 'Deleted';
  if (!hasKey) return 'No key in this browser';
  if (summary.messageCount === 0) return name ? 'Joined — say hello' : 'Joined';
  return `${summary.messageCount} message${summary.messageCount === 1 ? '' : 's'}`;
}

export function useConversationDirectory() {
  const [conversations, setConversations] = useState<ChatConversation[] | null>(null);

  const refresh = useCallback(async () => {
    const [list, stored, links] = await Promise.all([
      invitations.list(),
      listStoredConversationIds(),
      loadInvitationUrls(),
    ]);
    const known = new Set(stored);

    const rows = await Promise.all(
      list.conversations.map(async (summary): Promise<ChatConversation> => {
        const hasKey = known.has(summary.id);
        let name: string | null = null;

        if (hasKey && summary.visitorName) {
          const key = await loadConversationKey(summary.id);
          if (key) {
            try {
              const payload = await decryptPayload({
                key,
                conversationId: VISITOR_NAME_AAD_CID,
                sender: 'visitor',
                kind: 'visitor_name',
                envelope: summary.visitorName,
              });
              if (payload.kind === 'visitor_name') name = payload.displayName;
            } catch {
              name = null;
            }
          }
        }

        return {
          id: summary.id,
          name:
            summary.status === 'pending'
              ? 'Unused invitation'
              : (name ?? (hasKey ? 'Visitor' : 'Conversation')),
          preview: previewFor(summary, hasKey, name),
          timestamp: summary.lastActivityAt ?? summary.activatedAt ?? summary.createdAt,
          // No read state exists, so no unread count can be honestly derived.
          unread: 0,
          status: summary.status,
          hasKey,
          expiresAt: summary.expiresAt,
          inviteExpiresAt: summary.inviteExpiresAt,
          ...(links.get(summary.id) ? { invitationUrl: links.get(summary.id)! } : {}),
        };
      }),
    );

    // Most recently active first, which is what a messenger list means — but
    // invitations nobody has joined yet sink to the bottom. They carry no
    // activity, so sorting on time alone buried live conversations under links
    // the owner had only just created. The server orders the same way; this
    // keeps the two in step rather than silently overriding it.
    rows.sort((a, b) => {
      const pending = Number(a.status === 'pending') - Number(b.status === 'pending');
      if (pending !== 0) return pending;
      return (b.timestamp ?? '').localeCompare(a.timestamp ?? '');
    });
    setConversations(rows);
    return rows;
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { conversations, refresh };
}
